import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useLocation } from 'react-router-dom'
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Toolbar,
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import PersonIcon from '@mui/icons-material/Person'
import SchoolIcon from '@mui/icons-material/School'
import SettingsIcon from '@mui/icons-material/Settings'
import DateRangeIcon from '@mui/icons-material/DateRange'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LeaderboardIcon from '@mui/icons-material/Leaderboard'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball'
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth'
import { styled } from '@mui/material/styles'

import { REACT_HOST } from '../hooks/env-variables'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { setIsOpenDrawer } from '../container/home'

const drawerWidth = 220

const openedMixin = theme => ({
  width: drawerWidth,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
})

const closedMixin = theme => ({
  width: `calc(${theme.spacing(7)} + 1px)`,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: drawerWidth,
  whiteSpace: 'nowrap',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

const MyDivider = styled(Divider)(() => ({
  background: colours.drawerText,
}))

const menuItemHome = { label: 'Home', link: '/', icon: <HomeIcon /> }
const menuItemImpostazioni = {
  label: 'Impostazioni',
  link: '/impostazioni',
  icon: <SettingsIcon />,
}

const menuItems = {
  affiliazioni: { label: 'Società', link: '/affiliazioni', icon: 'SOC' },
  iscrizioni: { label: 'Iscrizioni', link: '/iscrizioni', icon: 'ISCR' },
  spostamenti: { label: 'Spostamenti', link: '/spostamenti', icon: 'SPO' },
  provvedimenti: {
    label: 'Provvedimenti',
    link: '/provvedimenti',
    icon: 'PRO',
  },
  rendiconti: { label: 'Rendiconti', link: '/rendiconti', icon: 'REN' },
  arbitro: { label: 'Profilo', link: '/arbitro', icon: <PersonIcon /> },
  arbitraggi: { label: 'Arbitraggi', link: '/arbitraggi', icon: 'ARB' },
  arbitri: { label: 'Arbitri', link: '/arbitri', icon: 'ARB' },
  designazioni: { label: 'Designazioni', link: '/designazioni', icon: 'DES' },
  rimborsi: { label: 'Rimborsi', link: '/rimborsi', icon: 'RIM' },
  squadre: {
    label: 'Squadre iscritte',
    link: '/squadre',
    icon: <SportsVolleyballIcon />,
  },
  gironi: { label: 'Gironi', link: '/gironi', icon: <CalendarViewMonthIcon /> },
  calendari: {
    label: 'Calendari',
    link: '/calendari',
    icon: <DateRangeIcon />,
  },
  classifiche: {
    label: 'Classifiche',
    link: '/classifiche',
    icon: <LeaderboardIcon />,
  },
  tabelloni: {
    label: 'Fasi finali',
    link: '/finali',
    icon: <AccountTreeIcon style={{ transform: 'rotate(180deg)' }} />,
  },
  algoritmi: {
    label: 'Algoritmi',
    link: '/algoritmi',
    icon: <TipsAndUpdatesIcon />,
  },
  corsi: {
    label: 'Corsi',
    link: '/iscrizioni-corsi',
    icon: <SchoolIcon />,
  },
  tesserati: { label: 'Tesserati', link: '/tesserati', icon: 'TES' },
  distinte: { label: 'Distinte', link: '/distinte', icon: 'DIS' },
}

const MyDrawer = () => {
  const infoUtente = useSelector(state => state.home.infoUtente)
  const isMobile = useSelector(state => state.home.isMobile)
  const isOpenDrawer = useSelector(state => state.home.isOpenDrawer)
  const { auth, admin, societa, arbitro, designatore } = infoUtente

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (REACT_HOST === 'localhost') dispatch(setIsOpenDrawer(false))
  }, [dispatch])

  const menuItems0 = [menuItemHome]

  let menuItems1 = []
  if (auth) {
    if (admin && config?.features?.affiliazioni)
      menuItems1.push(menuItems?.affiliazioni)

    if ((admin || societa) && config?.features?.iscrizioni)
      menuItems1.push(menuItems?.iscrizioni)

    if ((admin || societa) && config?.features?.spostamenti)
      menuItems1.push(menuItems?.spostamenti)

    if ((admin || designatore) && config?.features?.arbitraggi)
      menuItems1.push(menuItems?.arbitraggi)

    if (admin && config?.features?.provvedimenti)
      menuItems1.push(menuItems?.provvedimenti)

    if ((admin || societa) && config?.features?.rendiconti)
      menuItems1.push(menuItems?.rendiconti)

    if ((admin || societa || arbitro) && config?.features?.corsi)
      menuItems1.push(menuItems?.corsi)

    if (arbitro && config?.features?.arbitri)
      menuItems1.push(menuItems?.arbitro)

    if ((admin || designatore) && config?.features?.arbitri)
      menuItems1.push(menuItems?.arbitri)

    if ((admin || arbitro || designatore) && config?.features?.designazioni)
      menuItems1.push(menuItems?.designazioni)

    if ((admin || arbitro) && config?.features?.rimborsi)
      menuItems1.push(menuItems?.rimborsi)

    if ((admin || societa) && config?.features?.tesserati)
      menuItems1.push(menuItems?.tesserati)

    if ((admin || societa) && config?.features?.distinte)
      menuItems1.push(menuItems?.distinte)
  }

  let menuItems2 = []
  if (config?.features?.squadre) menuItems2.push(menuItems?.squadre)
  if (config?.features?.gironi) menuItems2.push(menuItems?.gironi)
  if (config?.features?.calendari) menuItems2.push(menuItems?.calendari)
  if (config?.features?.classifiche) menuItems2.push(menuItems?.classifiche)
  if (config?.features?.tabelloni) menuItems2.push(menuItems?.tabelloni)

  let menuItems3 = []
  if (admin) {
    if (config?.features?.algoritmi) menuItems3.push(menuItems?.algoritmi)

    menuItems3.push(menuItemImpostazioni)
  }

  const MyListItem = ({ item }) => (
    <ListItem
      disablePadding
      sx={{
        display: 'block',
        backgroundColor:
          item.link?.split('/')?.[1] === location.pathname?.split('/')?.[1] &&
          colours.blueLight,
        ':hover': { backgroundColor: colours.blue },
      }}
      onClick={() => {
        if (!!item?.func) item.func()
        if (!!item?.link) navigate(item.link)
        if (isMobile) dispatch(setIsOpenDrawer(false))
      }}
    >
      <ListItemButton>
        <ListItemIcon sx={{ color: colours.drawerText }}>
          {item?.icon || <ChevronRightIcon />}
        </ListItemIcon>
        <ListItemText
          primary={item?.label}
          slotProps={{
            primary: { color: colours.drawerText, fontWeight: 'bold' },
          }}
        />
      </ListItemButton>
    </ListItem>
  )

  const DrawerContent = (
    <>
      <Toolbar />
      {menuItems0?.length > 0 && (
        <List dense>
          {menuItems0.map(i => (
            <MyListItem key={i.label} item={i} />
          ))}
        </List>
      )}
      {menuItems1?.length > 0 && (
        <>
          <MyDivider />
          <List dense>
            {menuItems1.map(i => (
              <MyListItem key={i.label} item={i} />
            ))}
          </List>
        </>
      )}
      {menuItems2?.length > 0 && (
        <>
          <MyDivider />
          <List dense>
            {menuItems2.map(i => (
              <MyListItem key={i.label} item={i} />
            ))}
          </List>
        </>
      )}
      {menuItems3?.length > 0 && (
        <>
          <MyDivider />
          <List dense>
            {menuItems3.map(i => (
              <MyListItem key={i.label} item={i} />
            ))}
          </List>
        </>
      )}
      <MyDivider />
    </>
  )

  return isMobile ? (
    <SwipeableDrawer
      open={isOpenDrawer}
      onOpen={() => dispatch(setIsOpenDrawer(true))}
      onClose={() => dispatch(setIsOpenDrawer(false))}
      slotProps={{ paper: { sx: { backgroundColor: colours.blueDark } } }}
    >
      {DrawerContent}
    </SwipeableDrawer>
  ) : (
    <StyledDrawer
      variant='permanent'
      open={isOpenDrawer}
      slotProps={{ paper: { sx: { backgroundColor: colours.blueDark } } }}
    >
      {DrawerContent}
    </StyledDrawer>
  )
}

export default MyDrawer
